// The Terms are authored in legal/terminos-y-condiciones.md and stored here verbatim as
// Markdown, so the published document always matches the signed source.
// TERMS_VERSION is recorded against each signup as proof of which text was accepted;
// it must be bumped whenever the document changes.
export const TERMS_VERSION = "2026-08-13";
export const TERMS_EFFECTIVE_DATE = "13 de agosto de 2026";
export const TERMS_LAST_UPDATED_LABEL = "13 de agosto de 2026";

export const TERMS_MARKDOWN = `
# Términos y Condiciones de Voxara

**Fecha de entrada en vigor:** 13 de agosto de 2026
**Última actualización:** 13 de agosto de 2026
**Versión:** 2.0

---

## 0. Identificación del prestador del servicio

Voxara es operada por VOXARA S.A.S., sociedad constituida bajo las leyes de la República de Colombia, identificada con NIT 902.061.732-6, con domicilio principal en Carrera 71 # 127C-43, Bogotá D.C., Colombia.

**Correo de contacto:** lauradramirezabogada@gmail.com

En adelante, "Voxara", "nosotros", "nuestro" o "la Plataforma".

---

## 1. Introducción

Bienvenido a Voxara. Estos Términos y Condiciones de Uso regulan el acceso y uso de la plataforma, sitio web, aplicaciones, herramientas, funciones de transcripción, análisis, resumen, exportación y chat con inteligencia artificial ofrecidos por Voxara.

Al crear una cuenta, acceder o usar Voxara, usted acepta quedar obligado por estos Términos. Si no está de acuerdo, no debe usar la Plataforma.

Estos Términos se rigen por la legislación colombiana, incluyendo la Ley 527 de 1999 (comercio electrónico), la Ley 1480 de 2011 (Estatuto del Consumidor) y la Ley 1581 de 2012 (protección de datos personales).

---

## 2. Descripción del servicio

Voxara es una plataforma que permite, entre otras funciones:

- subir archivos de video y audio;
- transcribir contenido mediante inteligencia artificial;
- identificar hablantes, marcas de tiempo y momentos clave;
- generar resúmenes;
- permitir preguntas y respuestas sobre el contenido cargado;
- exportar resultados en distintos formatos, incluyendo texto, PDF y Word, cuando estén disponibles.

Voxara puede modificar, mejorar, suspender o descontinuar funciones. Cuando un cambio afecte de forma sustancial y desfavorable las funciones incluidas en un plan pago vigente, se lo informaremos con al menos treinta (30) días de anticipación y usted podrá cancelar con reembolso proporcional del periodo no usado.

---

## 3. Elegibilidad

Para usar Voxara, usted declara que:

- tiene al menos 18 años cumplidos;
- tiene capacidad legal para aceptar estos Términos;
- usará la Plataforma de conformidad con la ley aplicable.

Voxara no está dirigida a menores de edad. Si usa Voxara en nombre de una empresa u organización, declara que tiene autoridad suficiente para obligarla a estos Términos.

---

## 4. Cuenta y seguridad

Para acceder a ciertas funciones, es necesario crear una cuenta. Usted es responsable de:

- proporcionar información verdadera y actualizada;
- mantener la confidencialidad de sus credenciales;
- toda actividad realizada desde su cuenta.

Debe notificarnos de inmediato a lauradramirezabogada@gmail.com si sospecha acceso no autorizado o uso indebido de su cuenta.

---

## 5. Contenido del usuario

"Contenido del Usuario" incluye cualquier archivo, video, audio, texto, instrucción, pregunta, documento o material que usted cargue, envíe o procese a través de Voxara.

Usted conserva la propiedad de su Contenido del Usuario. Al usar Voxara, nos otorga una licencia limitada, no exclusiva, revocable y **restringida a la prestación del servicio** para alojar, procesar, transmitir, analizar, transcribir, convertir, almacenar y mostrar dicho contenido.

**Esta licencia no incluye el uso de su contenido para entrenar modelos de inteligencia artificial, ni propios ni de terceros.** La licencia termina cuando usted elimina el contenido o cierra su cuenta, salvo por las copias que debamos conservar por obligación legal según la sección 12.

Usted declara y garantiza que:

- posee los derechos necesarios sobre el contenido que sube;
- cuenta con la autorización de las personas cuya voz, imagen o datos personales aparezcan en el contenido, cuando la ley lo exija;
- el contenido no infringe derechos de terceros ni viola la ley.

---

## 6. Rol de las partes en el tratamiento de datos personales

Cuando el Contenido del Usuario contiene datos personales de terceros (por ejemplo, las voces y los nombres de los participantes de una reunión grabada):

- **Usted actúa como Responsable del Tratamiento.** Le corresponde obtener la autorización de los titulares, informarles la finalidad y atender sus derechos.
- **Voxara actúa como Encargado del Tratamiento**, y trata esos datos únicamente siguiendo sus instrucciones y para prestarle el servicio.

Respecto de los datos de su propia cuenta (nombre, correo, facturación, uso), Voxara actúa como Responsable.

Si usted es una empresa y necesita un acuerdo de transmisión de datos personales por escrito, puede solicitarlo a lauradramirezabogada@gmail.com.

---

## 7. Uso permitido

Usted acepta usar Voxara solo para fines legales y autorizados. No podrá:

- subir contenido ilícito, fraudulento, difamatorio, abusivo o que viole derechos de terceros;
- subir contenido protegido sin autorización;
- usar la Plataforma para vigilancia ilegal, invasión de privacidad o recopilación indebida de datos;
- interceptar o grabar comunicaciones sin la autorización que exija la ley aplicable;
- intentar acceder sin autorización a sistemas, cuentas o infraestructura;
- interferir con la seguridad, estabilidad o funcionamiento de Voxara;
- realizar ingeniería inversa, copiar, revender o explotar indebidamente la Plataforma;
- usar bots, automatizaciones o scripts de forma abusiva que afecten el servicio;
- usar la Plataforma para generar o distribuir spam, malware o contenido dañino.

Nos reservamos el derecho de suspender o cancelar cuentas que infrinjan estas reglas, conforme al procedimiento de la sección 20.

---

## 8. Procesamiento por inteligencia artificial

Voxara utiliza sistemas automatizados e inteligencia artificial, propios y de proveedores externos, para generar transcripciones, resúmenes, respuestas, marcas de tiempo y otras salidas. Los proveedores concretos están identificados en la Política de Privacidad, sección 6.

Usted reconoce y acepta que:

- los resultados pueden contener errores, omisiones o interpretaciones incorrectas;
- la identificación de hablantes, tiempos, nombres, citas o resúmenes puede no ser perfecta;
- las respuestas del asistente de IA se generan con base en el contenido disponible y pueden ser incompletas;
- Voxara no garantiza exactitud absoluta.

Usted es responsable de revisar y validar cualquier resultado antes de usarlo para fines legales, comerciales, médicos, regulatorios, académicos o de alto impacto. **Voxara no es un sustituto de asesoría profesional ni de una transcripción certificada o con valor probatorio.**

Voxara no toma decisiones automatizadas que produzcan efectos jurídicos sobre usted.

---

## 9. Contenido sensible y cumplimiento

Usted no debe usar Voxara para procesar contenido si no tiene base legal o permiso suficiente para hacerlo, incluyendo contenido que contenga:

- datos personales sensibles (salud, biometría, origen étnico, orientación sexual, convicciones políticas o religiosas, entre otros);
- información médica o confidencial;
- secretos comerciales;
- grabaciones privadas o restringidas;
- evidencia, audiencias, reuniones o comunicaciones sujetas a reserva legal o contractual.

Si usa Voxara para dicho contenido, lo hace bajo su propia responsabilidad y garantiza que cuenta con todas las autorizaciones necesarias, incluida la autorización explícita que el artículo 6 de la Ley 1581 de 2012 exige para datos sensibles.

---

## 10. Planes, suscripciones y pagos

Algunas funciones de Voxara requieren pago, suscripción mensual, anual o por uso.

- Los precios se muestran en la moneda indicada al momento de la compra e **incluyen el IVA aplicable** cuando corresponda.
- Los pagos se procesan a través de MercadoPago. Voxara no almacena los datos completos de su tarjeta.
- Voxara expedirá factura electrónica conforme a la normativa de la DIAN.
- Los límites de uso varían según el plan contratado.
- Las suscripciones se renuevan automáticamente hasta que sean canceladas. **Le avisaremos por correo electrónico al menos cinco (5) días antes de cada renovación**, indicando el monto y la fecha del cobro.
- Los cambios de precio se comunicarán con al menos treinta (30) días de anticipación y solo aplicarán a periodos de facturación posteriores. Usted podrá cancelar antes de que entren en vigor.

Usted autoriza a Voxara y a sus procesadores de pago a cobrar los importes aplicables, impuestos y renovaciones según su plan.

---

## 11. Cancelación, retracto y reembolsos

### 11.1 Cancelación

Usted puede cancelar su suscripción en cualquier momento desde su cuenta, sin necesidad de contactar a soporte y sin penalidad. La cancelación evita renovaciones futuras y mantiene su acceso hasta el final del periodo ya pagado.

### 11.2 Derecho de retracto (Ley 1480 de 2011, art. 47)

Si usted es consumidor y contrató un plan a distancia, tiene derecho a retractarse **dentro de los cinco (5) días hábiles siguientes a la contratación**, sin necesidad de justificación, y a que se le devuelva la totalidad de lo pagado.

Para ejercerlo, escriba a lauradramirezabogada@gmail.com desde el correo de su cuenta. Devolveremos el dinero a más tardar dentro de los treinta (30) días calendario siguientes.

### 11.3 Reversión del pago (Ley 1480 de 2011, art. 51)

Usted puede solicitar la reversión del pago en los casos previstos por la ley, incluyendo fraude, operación no solicitada o servicio no prestado. Dirija la solicitud simultáneamente a Voxara y a su emisor de tarjeta dentro de los cinco (5) días hábiles siguientes a que tenga conocimiento del hecho.

### 11.4 Otros reembolsos

Fuera de los casos anteriores, y salvo que la ley aplicable disponga otra cosa, los pagos por periodos ya iniciados no son reembolsables ni se prorratean. Voxara podrá otorgar reembolsos excepcionales a su discreción.

**Ninguna disposición de esta sección limita los derechos irrenunciables que la ley colombiana reconoce a los consumidores.**

---

## 12. Almacenamiento y retención

Voxara almacena archivos, transcripciones, metadatos, resultados, historial de chat y exportaciones conforme a los plazos detallados en la **sección 8 de la Política de Privacidad**, que forma parte integral de estos Términos.

En resumen: los archivos de video y audio originales se eliminan de nuestra infraestructura una vez finalizado el procesamiento; las transcripciones y resultados permanecen disponibles en su cuenta hasta que usted los elimine o cierre la cuenta.

Nos reservamos el derecho de establecer límites de tamaño de archivo, duración, almacenamiento total, cantidad de proyectos e historial de consultas, informándolos en la página de planes.

Usted es responsable de descargar y conservar sus propias copias cuando lo considere necesario.

---

## 13. Propiedad intelectual

Voxara y todos los elementos de la Plataforma, incluyendo software, diseño, interfaz, marca, logotipos, flujos, textos, código, funcionalidades y documentación, son propiedad de Voxara o de sus licenciantes y están protegidos por las leyes de propiedad intelectual, incluyendo la Decisión Andina 351 de 1993 y la Ley 23 de 1982.

Estos Términos no le transfieren ningún derecho de propiedad sobre Voxara. Solo le otorgamos un derecho limitado, revocable, no exclusivo e intransferible para usar la Plataforma conforme a estos Términos.

**Las transcripciones, resúmenes y demás resultados generados a partir de su Contenido del Usuario le pertenecen a usted.** Voxara no reclama derechos sobre ellos.

---

## 14. Comentarios y mejoras

Si usted envía sugerencias, ideas o comentarios sobre Voxara, podremos utilizarlos libremente para mejorar el servicio, sin obligación de compensación, reconocimiento o pago.

---

## 15. Servicios de terceros

Voxara depende de proveedores externos para funcionalidades como procesamiento de pagos, inteligencia artificial, autenticación, almacenamiento, correo electrónico y hosting. La identidad de estos proveedores se detalla en la Política de Privacidad.

No somos responsables por fallas, cambios, interrupciones o actos de terceros fuera de nuestro control razonable. Esto no nos exime de nuestra responsabilidad como Responsable del Tratamiento frente a los datos personales que confiamos a dichos proveedores.

---

## 16. Privacidad

El uso de Voxara está sujeto a nuestra Política de Privacidad, que explica cómo recopilamos, usamos y protegemos la información. Al usar la Plataforma, usted reconoce haber leído y entendido dicha política.

---

## 17. Disponibilidad del servicio

Aunque buscamos mantener Voxara disponible y funcional, no garantizamos que la Plataforma estará libre de interrupciones, demoras o errores.

El servicio puede verse afectado por mantenimiento, fallas técnicas, sobrecarga, problemas con terceros o cambios en APIs, infraestructura, proveedores de IA o servicios de almacenamiento.

Podemos suspender temporalmente el acceso por razones operativas, de seguridad o mantenimiento, procurando avisar con anticipación cuando sea posible.

---

## 18. Exención de garantías

Voxara se ofrece "tal cual" y "según disponibilidad". En la máxima medida permitida por la ley, no ofrecemos garantías expresas o implícitas de comerciabilidad, idoneidad para un propósito particular, no infracción, disponibilidad continua, exactitud o confiabilidad.

**Esta exención no aplica a la garantía legal que la Ley 1480 de 2011 reconoce a los consumidores en Colombia, la cual es irrenunciable.**

---

## 19. Limitación de responsabilidad

En la máxima medida permitida por la ley, Voxara, sus directores, empleados, afiliados, contratistas y proveedores no serán responsables por daños indirectos, incidentales, especiales, consecuenciales, ejemplares o pérdida de beneficios, ingresos, datos, reputación u oportunidades, derivados del uso o imposibilidad de uso de la Plataforma.

En ningún caso la responsabilidad total de Voxara excederá el monto efectivamente pagado por usted a Voxara durante los doce (12) meses anteriores al hecho que origine la reclamación, o el equivalente en pesos colombianos a cien dólares estadounidenses (USD $100), lo que sea mayor.

**Estas limitaciones no aplican en casos de dolo o culpa grave, ni frente a los derechos irrenunciables del consumidor bajo la Ley 1480 de 2011, ni respecto de las obligaciones de Voxara bajo la Ley 1581 de 2012.**

---

## 20. Indemnización

Usted acepta defender, indemnizar y mantener indemne a Voxara frente a reclamaciones, daños, pérdidas, responsabilidades, costos y gastos derivados de:

- su uso de la Plataforma;
- su Contenido del Usuario, incluida la falta de autorización de los titulares de datos personales que aparezcan en él;
- su incumplimiento de estos Términos;
- su violación de derechos de terceros;
- su incumplimiento de leyes o regulaciones aplicables.

---

## 21. Suspensión y terminación

Podemos suspender, restringir o terminar su acceso a Voxara si consideramos que ha violado estos Términos, existe riesgo legal o de seguridad, su uso afecta a la Plataforma o a terceros, o debemos cumplir requerimientos legales.

**Salvo en casos de riesgo grave e inminente, ilegalidad manifiesta u orden de autoridad competente, le notificaremos previamente al correo de su cuenta**, indicando el motivo y dándole una oportunidad razonable de subsanar o de presentar sus explicaciones. Usted puede apelar la decisión escribiendo a lauradramirezabogada@gmail.com.

Si terminamos su cuenta sin que exista incumplimiento de su parte, le reembolsaremos la parte proporcional del periodo pagado y no usado.

Tras la terminación, dispondrá de treinta (30) días calendario para exportar su contenido antes de su eliminación definitiva.

Las disposiciones que por su naturaleza deban sobrevivir a la terminación seguirán vigentes.

---

## 22. Cambios a estos Términos

Podemos modificar estos Términos ocasionalmente.

- Para **cambios menores** (correcciones, aclaraciones), actualizaremos la fecha de última modificación.
- Para **cambios sustanciales** que afecten sus derechos u obligaciones, se lo notificaremos al correo de su cuenta y dentro de la Plataforma con al menos **treinta (30) días** de anticipación. Si no está de acuerdo, puede cancelar antes de la entrada en vigor y recibir el reembolso proporcional del periodo no usado.

Conservamos las versiones anteriores de este documento y podemos entregárselas si lo solicita.

---

## 23. Atención al consumidor y solución de controversias

Antes de acudir a instancias externas, le pedimos escribirnos a lauradramirezabogada@gmail.com. Responderemos las peticiones, quejas y reclamos dentro de los quince (15) días hábiles siguientes a su recepción.

Si no queda conforme, puede presentar su reclamación ante la **Superintendencia de Industria y Comercio (SIC)** a través de sus canales oficiales.

---

## 24. Ley aplicable y jurisdicción

**Estos Términos se rigen por las leyes de la República de Colombia**, sin perjuicio de sus normas sobre conflicto de leyes.

Cualquier controversia derivada de estos Términos se someterá a los **jueces y tribunales competentes de la República de Colombia**.

Cuando usted actúe como consumidor, conserva el derecho de acudir al juez de su domicilio y a las autoridades administrativas competentes, sin que esta cláusula pueda entenderse como una renuncia a dicho derecho.

Si usted reside fuera de Colombia, esta elección de ley no le priva de la protección que le otorguen las disposiciones imperativas de la ley de su país de residencia habitual.

---

## 25. Disposiciones generales

Si alguna disposición de estos Términos se considera inválida o inaplicable, las demás seguirán en pleno vigor y la disposición afectada se interpretará en el sentido más próximo permitido por la ley.

La falta de ejercicio de cualquier derecho por parte de Voxara no constituirá renuncia.

Usted no podrá ceder estos Términos sin autorización previa por escrito. Voxara podrá cederlos en caso de reorganización, fusión, venta o transferencia del negocio, informándoselo con antelación razonable.

Estos Términos, junto con la Política de Privacidad, constituyen el acuerdo íntegro entre usted y Voxara respecto del uso de la Plataforma.

---

## 26. Contacto

VOXARA S.A.S. — NIT 902.061.732-6
Carrera 71 # 127C-43, Bogotá D.C., Colombia
Correo: lauradramirezabogada@gmail.com
Teléfono: +57 320 408 6741
`;
